import React from 'react'

interface NavItem {
  label: string
  icon?: string
  active?: boolean
  current?: boolean
  onClick?: () => void
  href?: string
}

interface NavigationProps {
  items?: NavItem[]
  tabs?: { label: string; value: string; emoji?: string }[]
  activeTab?: string
  onTabChange?: (value: string) => void
}

export function TabNavigation({ items, tabs, activeTab, onTabChange }: NavigationProps): React.ReactElement {
  // Support tabs format
  if (tabs && onTabChange) {
    return (
      <div className="flex gap-2 mb-8 flex-wrap">
        {tabs.map((tab, idx) => (
          <button
            key={idx}
            onClick={() => onTabChange(tab.value)}
            className={`px-6 py-3 rounded-2xl font-black text-lg transition transform hover:scale-105 ${
              activeTab === tab.value
                ? 'bg-gradient-to-r from-pink-400 to-purple-400 text-white shadow-lg'
                : 'bg-white border-3 border-purple-200 text-purple-700 hover:border-pink-400'
            }`}
          >
            {tab.emoji && `${tab.emoji} `}{tab.label}
          </button>
        ))}
      </div>
    )
  }

  // Support items format
  if (!items) return <div />

  return (
    <div className="flex gap-2 mb-8 flex-wrap">
      {items.map((item, idx) => (
        <button
          key={idx}
          onClick={item.onClick}
          className={`px-6 py-3 rounded-2xl font-black text-lg transition transform hover:scale-105 ${
            item.active || item.current
              ? 'bg-gradient-to-r from-pink-400 to-purple-400 text-white shadow-lg'
              : 'bg-white border-3 border-purple-200 text-purple-700 hover:border-pink-400'
          }`}
        >
          {item.icon && `${item.icon} `}{item.label}
        </button>
      ))}
    </div>
  )
}

interface TabContent {
  label: string
  icon: string
  value: string
}

interface VerticalTabsProps {
  tabs: TabContent[]
  activeTab: string
  onTabChange: (value: string) => void
  children: React.ReactNode
}

export function VerticalTabs({ tabs, activeTab, onTabChange, children }: VerticalTabsProps): React.ReactElement {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      <div className="lg:col-span-1">
        <div className="bg-white rounded-2xl border-3 border-purple-200 p-4 shadow-lg">
          {tabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => onTabChange(tab.value)}
              className={`w-full text-left px-6 py-4 rounded-xl font-black mb-2 transition transform ${
                activeTab === tab.value
                  ? 'bg-gradient-to-r from-pink-400 to-purple-400 text-white shadow-lg'
                  : 'hover:bg-purple-50 text-purple-700'
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>
      </div>
      <div className="lg:col-span-3">
        {children}
      </div>
    </div>
  )
}

interface BreadcrumbProps {
  items: { label: string; onClick?: () => void }[]
}

export function Breadcrumb({ items }: BreadcrumbProps): React.ReactElement {
  return (
    <div className="flex items-center gap-2 mb-6 font-bold text-purple-700">
      {items.map((item, idx) => (
        <div key={idx} className="flex items-center gap-2">
          {item.onClick ? (
            <button onClick={item.onClick} className="hover:text-pink-600">
              {item.label}
            </button>
          ) : (
            <span>{item.label}</span>
          )}
          {idx < items.length - 1 && <span>→</span>}
        </div>
      ))}
    </div>
  )
}
